import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const values = [
  {
    icon: "ri-book-open-line",
    title: "Reading as Identity",
    desc: "We don't just want you to read more books. We want reading to become part of who you are — as natural as breathing.",
  },
  {
    icon: "ri-seedling-line",
    title: "Small Over Big",
    desc: "Grand ambitions fade. Small, consistent actions compound. We design habits that are easy to start and impossible to quit.",
  },
  {
    icon: "ri-heart-line",
    title: "Joy Over Obligation",
    desc: "Reading should feel like a gift, not a chore. We help you rediscover the pure pleasure of getting lost in a book.",
  },
  {
    icon: "ri-time-line",
    title: "Long-term Thinking",
    desc: "We play the long game. One year of 10 pages a day beats five years of sporadic, guilt-driven reading marathons.",
  },
];

export default function About() {
  return (
    <main className="bg-[#F4F3F0]">
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-40 pb-24 px-8 lg:px-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a5d061ce3344617437622a6c3cf7f848.jpg"
            alt="Library"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#F4F3F0]/90" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-widest mb-4 block">Our Story</span>
          <h1 className="font-serif text-5xl lg:text-6xl text-[#1A1A1A] leading-[1.15] mb-6">
            We started with <em>one simple idea.</em>
          </h1>
          <p className="text-[#555] text-lg leading-relaxed">
            What if the secret to reading more wasn't motivation, discipline, or the perfect reading list — but simply lowering the bar to 10 pages a day?
          </p>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-20 px-8 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="w-full h-96 rounded-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=person%20sitting%20in%20a%20cozy%20reading%20chair%20by%20a%20window%20with%20natural%20light%20holding%20an%20open%20book%2C%20peaceful%20morning%20reading%20scene%2C%20warm%20cream%20and%20natural%20tones%2C%20calm%20atmosphere%2C%20lifestyle%20photography%20with%20soft%20bokeh%20background%20and%20white%20walls&width=800&height=600&seq=about-origin-1&orientation=portrait"
              alt="Reading by window"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-widest mb-4 block">The Beginning</span>
            <h2 className="font-serif text-4xl text-[#1A1A1A] leading-[1.25] mb-6">
              Born from the belief that showing up is enough.
            </h2>
            <p className="text-[#555] leading-relaxed mb-4">
              This brand didn't start with a product. It started with a question: what does it look like to genuinely show up for yourself — not when motivation peaks, not after a fresh start, but on an ordinary Tuesday?
            </p>
            <p className="text-[#555] leading-relaxed mb-4">
              We found the answer in reading. Not reading as a performance, not a list to check off — but 10 quiet pages as a daily act of self-respect. A promise you keep to yourself, even when nothing else goes right.
            </p>
            <p className="text-[#555] leading-relaxed mb-8">
              That identity — the person who shows up daily — is what we build everything around. The tools, the rituals, the community. All of it exists to remind you: you are someone who does the thing.
            </p>
            <p className="text-[#888] text-sm border-l-2 border-[#3A5544] pl-4">
              "It's not about the books you finish. It's about the person you become by never stopping."
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-8 lg:px-24 bg-[#F4F3F0]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-widest mb-3 block">What We Stand For</span>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#1A1A1A]">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-lg p-8">
                <div className="w-10 h-10 flex items-center justify-center mb-4">
                  <i className={`${v.icon} text-2xl text-[#3A5544]`} />
                </div>
                <h3 className="font-semibold text-[#1A1A1A] mb-3">{v.title}</h3>
                <p className="text-[#666] text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-8 lg:px-24 bg-[#111111]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-4xl text-white mb-6">Ready to join us?</h2>
          <p className="text-white/55 leading-relaxed mb-10">
            Thousands of readers have already started their 10-page journey. Come find out what consistent reading feels like.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#3A5544] text-white px-8 py-4 rounded font-medium hover:bg-[#2D4238] transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Get in Touch <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
